import { useState, useEffect, useCallback } from 'react';
import { User, CreateUserRequest, UpdateUserRequest } from '../types/user';
import { userApi } from '../api/userApi';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching users...');
      const response = await userApi.getUsers(1, 1000); // Fetch first 1000 users
      console.log('Users API response:', response);
      
      // The response is already properly formatted by userApi.getUsers()
      const usersList = response.items || [];
      console.log('Setting users:', usersList);
      setUsers(usersList);
      setError(null);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createUser = async (userData: CreateUserRequest) => {
    try {
      const newUser = await userApi.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      return newUser; // Return the created user directly
    } catch (err) {
      console.error('Error creating user:', err);
      throw err; // Rethrow the error to be handled by the component
    }
  };

  const updateUser = async (id: string, userData: UpdateUserRequest) => {
    try {
      const updatedUser = await userApi.updateUser(id, userData);
      setUsers(prev => prev.map(user => 
        user.id === id ? updatedUser : user
      ));
      return updatedUser;
    } catch (err) {
      console.error('Error updating user:', err);
      throw err; // Rethrow the error to be handled by the component
    }
  };

  const deleteUser = useCallback(async (id: string): Promise<boolean> => {
    try {
      await userApi.deleteUser(id);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== id));
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
      return false;
    }
  }, []);

  const resetUserPassword = useCallback(async (id: string): Promise<boolean> => {
    try {
      const user = users.find(u => u.id === id);
      if (!user) throw new Error('User not found');
      
      // Use the dedicated API method for password reset
      await userApi.resetUserPassword(id, user.phoneNumber || '');
      
      // Refresh the users list
      await fetchUsers();
      return true;
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Failed to reset password');
      throw error; // Rethrow the error to be handled by the component
    }
  }, [users, fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    fetchUsers,
  };
};

export default useUsers;
